import { Grid, Switch, Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";
import translate from "i18n/translate";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import ApiRequest from "../../../../actions/ApiRequest";
import Toast from "components/common/Toast";

// Carriers whose product table has an `active` column keyed on
// Operator_Card_ID. Anything else renders without the switch rather than
// offering a control that cannot work - paltel_settings holds settings
// rather than products, and has neither column.
const TOGGLEABLE_CARRIERS = [
  "cellcom",
  "pelephone",
  "partner",
  "golan telecom",
  "012 mobile",
  "jawwal",
  "hot",
  "azy",
  "ooredoo",
];

const isEnabled = (item) =>
  String(item?.active ?? "true").trim().toLowerCase() !== "false";

const productIdOf = (item) =>
  String(item?.Operator_Card_ID ?? item?.ProviderCardID ?? "").trim();

export default function OperatorProducts({ data, carrier, defaultImg }) {
  const history = useHistory();
  const canToggle = TOGGLEABLE_CARRIERS.includes(
    String(carrier || "").toLowerCase()
  );

  // Held locally so the switch responds immediately, then reconciled against
  // what the server actually stored.
  const [activeById, setActiveById] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    const next = {};
    (data || []).forEach((item) => {
      const id = productIdOf(item);
      if (id) next[id] = isEnabled(item);
    });
    setActiveById(next);
  }, [data]);

  const handleItemClick = (itemId) => {
    history.push(`/admin/editSettings`, {
      data: data.find((item) => item.ProviderCardID === itemId),
      carrier,
    });
  };

  const handleToggle = async (productId, nextEnabled) => {
    setSavingId(productId);
    setActiveById((prev) => ({ ...prev, [productId]: nextEnabled }));

    try {
      const res = await ApiRequest.post(
        `set_product_active?carrier=${encodeURIComponent(
          carrier
        )}&product_id=${encodeURIComponent(productId)}&enabled=${
          nextEnabled ? "true" : "false"
        }`
      );

      if (res?.data?.status === "success") {
        Toast.fire({
          title: nextEnabled ? "Card enabled" : "Card disabled",
          icon: "success",
        });
      } else {
        setActiveById((prev) => ({ ...prev, [productId]: !nextEnabled }));
        Toast.fire({
          title: res?.data?.reason || "Something Went Wrong!",
          icon: "error",
        });
      }
    } catch (err) {
      setActiveById((prev) => ({ ...prev, [productId]: !nextEnabled }));
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Grid
      container
      spacing={2}
      mt={2}
      sx={{ display: "flex", alignItems: "stretch" }}
    >
      {data?.map((item, index) => {
        const productId = productIdOf(item);
        const enabled = activeById[productId] ?? isEnabled(item);
        const showSwitch = canToggle && Boolean(productId);

        return (
          <Grid item key={index} xs={6} sm={4} md={3} lg={2}>
            <Box
              sx={{
                padding: "12px",
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
                textAlign: "center",
                transition: "all 0.2s ease",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                border: enabled ? "1px solid transparent" : "1px dashed #9ca3af",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0px 4px 12px rgba(4, 120, 87, 0.2)",
                },
              }}
            >
              <Box
                onClick={() => handleItemClick(item.ProviderCardID)}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  // A disabled card stays legible but reads as switched off.
                  opacity: enabled ? 1 : 0.45,
                }}
              >
                <img
                  src={item.url || defaultImg}
                  alt={item.Provider || carrier}
                  style={{
                    height: "80px",
                    width: "auto",
                    maxWidth: "100%",
                    objectFit: "contain",
                    marginBottom: "8px",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#374151",
                    lineHeight: 1.3,
                  }}
                >
                  {item.Provider
                    ? `${item.Provider} = ${item.Amount} ILS`
                    : `Settings = ${item.Settings}`}
                </Typography>
              </Box>

              {showSwitch && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                    marginTop: "6px",
                  }}
                >
                  <Tooltip
                    title={
                      enabled
                        ? "Shown to sellers - click to hide"
                        : "Hidden from sellers - click to show"
                    }
                  >
                    <Switch
                      size="small"
                      color="success"
                      checked={enabled}
                      disabled={savingId === productId}
                      inputProps={{ "aria-label": `product ${productId}` }}
                      onChange={(e) => handleToggle(productId, e.target.checked)}
                    />
                  </Tooltip>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "11px", color: "#6b7280" }}
                  >
                    {enabled ? translate("enabled") : translate("disabled")}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}
